import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../../../js/api.js";
import { normalizarEmpresaResposta } from "../utils/empresa.js";
import { obterTipoProgramaItem } from "../mappers/empresaMapper.js";
import {
  cadastroRequerSupex,
  normalizarListaSolicitacoesExcecao,
} from "../mappers/supexMapper.js";
import { obterTipoProgramaUsuarioLogado } from "../utils/tipoPrograma.js";
import { aplicarMetadadosPrograma } from "./acompanhamentoHelpers.js";

/**
 * Resolve o programa efetivo (0 = TCE, 1 = TCA, 2 = TCA Empregador) para um item de acompanhamento.
 */
function resolverProgramaEfetivo(empresaRef, raw, token) {
  const tipoProgramaItem =
    obterTipoProgramaItem(empresaRef) ??
    obterTipoProgramaItem(empresaRef?.raw) ??
    (() => {
      const bruto =
        empresaRef?.tipo_programa ??
        empresaRef?.tipoPrograma ??
        raw?.tipo_programa ??
        raw?.tipoPrograma;
      return bruto !== undefined && bruto !== null && bruto !== ""
        ? Number(bruto)
        : null;
    })();

  let tokenDecoded = null;
  try {
    const t =
      token || (typeof window !== "undefined" ? localStorage.getItem("token") : "");
    if (t) tokenDecoded = jwtDecode(t);
  } catch {
    tokenDecoded = null;
  }
  const tipoProgramaJwt = Number(tokenDecoded?.tipo_programa);
  const tipoProgramaLogado = obterTipoProgramaUsuarioLogado();

  const origemItem = String(
    empresaRef?._origem || raw?._origem || empresaRef?.raw?._origem || "",
  ).toLowerCase();

  const isTceDireto =
    origemItem === "tce" ||
    origemItem === "estagio" ||
    empresaRef?.tce_id != null ||
    raw?.tce_id != null;

  if (isTceDireto) return 0;

  if (
    tipoProgramaItem !== null &&
    tipoProgramaItem !== undefined &&
    (tipoProgramaItem === 0 || tipoProgramaItem === 1 || tipoProgramaItem === 2)
  ) {
    return tipoProgramaItem;
  }

  if (tipoProgramaLogado === 0) return 0;
  if (tipoProgramaLogado === 2) return 2;

  if (
    Number.isInteger(tipoProgramaJwt) &&
    (tipoProgramaJwt === 0 || tipoProgramaJwt === 1 || tipoProgramaJwt === 2)
  ) {
    return tipoProgramaJwt;
  }

  const statusStr = String(
    empresaRef?.status || raw?.status || "",
  ).toUpperCase();
  if (
    statusStr.includes("ESTUDANTE") ||
    statusStr.includes("APRENDIZ") ||
    empresaRef?.tca_empregador_id != null ||
    raw?.tca_empregador_id != null
  ) {
    return 2;
  }

  return tipoProgramaLogado;
}

/**
 * Resolve o ID do contrato/autorização a ser consultado.
 */
function resolverTargetId(empresaRef, programaEfetivo, origemItem, isTceDireto) {
  if (isTceDireto || programaEfetivo === 0) {
    return empresaRef.tce_id ?? empresaRef.id;
  }
  if (
    programaEfetivo === 2 ||
    origemItem === "tca_empregador" ||
    origemItem === "tca-empregador"
  ) {
    return (
      empresaRef.tca_empregador_id ??
      empresaRef.tca_id ??
      empresaRef.tcaId ??
      empresaRef.id
    );
  }
  return empresaRef.tca_id ?? empresaRef.tcaId ?? empresaRef.id;
}

/**
 * Resolve a URL final do endpoint de detalhes.
 */
function resolverEndpointDetalhes(origemItem, programaEfetivo, isTceDireto, targetId) {
  if (
    origemItem === "tca_empregador" ||
    origemItem === "tca-empregador" ||
    programaEfetivo === 2
  ) {
    return `${API_BASE_URL}/tca-empregador/${targetId}/status-completo`;
  }
  if (isTceDireto || programaEfetivo === 0) {
    return `${API_BASE_URL}/tce/${targetId}`;
  }
  if (origemItem === "tca" || programaEfetivo === 1) {
    return `${API_BASE_URL}/tca/${targetId}`;
  }
  return `${API_BASE_URL}/tca-empregador/${targetId}/status-completo`;
}

/**
 * Atualiza o status de uma empresa específica e devolve um modelo
 * pronto para a UI (passando pelos mappers de SUPEX, rejeição e status).
 *
 * @param {object} empresaRef
 * @param {string} token
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ payload: object, empresa: object, listaExcecoes: Array, fonte: "api"|"cache" }>}
 */
const inFlightStatusPromises = new Map();

export async function buscarStatusCompletoDaEmpresa(empresaRef, token, signal) {
  if (!empresaRef) throw new Error("Empresa inválida");

  const raw = empresaRef.raw || empresaRef || {};
  const programaEfetivo = resolverProgramaEfetivo(empresaRef, raw, token);

  const origemItem = String(
    empresaRef?._origem || raw?._origem || empresaRef?.raw?._origem || "",
  ).toLowerCase();

  const isTceDireto =
    origemItem === "tce" ||
    origemItem === "estagio" ||
    empresaRef?.tce_id != null ||
    raw?.tce_id != null;

  const targetId = resolverTargetId(
    empresaRef,
    programaEfetivo,
    origemItem,
    isTceDireto,
  );

  // Sem targetId válido -> retorna com base no cache em memória (raw)
  if (!targetId || targetId === "undefined" || targetId === "null") {
    const payload = aplicarMetadadosPrograma(raw, programaEfetivo);
    const empresa = normalizarEmpresaResposta(payload) || payload;
    const listaExcecoes = normalizarListaSolicitacoesExcecao(payload, empresa);

    return {
      payload,
      empresa,
      listaExcecoes,
      fonte: "cache",
    };
  }

  const cacheKey = `${programaEfetivo}:${targetId}`;

  if (inFlightStatusPromises.has(cacheKey)) {
    return inFlightStatusPromises.get(cacheKey);
  }

  const endpoint = resolverEndpointDetalhes(
    origemItem,
    programaEfetivo,
    isTceDireto,
    targetId,
  );

  const promiseExecucao = (async () => {
    try {
      let resp;
      try {
        resp = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
          signal,
        });
      } catch (errReq) {
        // Se bateu em /tca/:id e retornou 403, 404 ou erro, tenta /tca-empregador/:id
        // Se bateu em /tca-empregador/:id e retornou 404, tenta /tca/:id
        // NUNCA tenta fallback para tca quando o contexto é TCE (0)!
        const statusErroReq = errReq?.response?.status;
        const isProgramaTce = programaEfetivo === 0 || isTceDireto;
        const deveTentarFallbackTcaEmpregador =
          !isProgramaTce &&
          (statusErroReq === 404 || statusErroReq === 403) &&
          !endpoint.includes("/tca-empregador");
        const deveTentarFallbackTcaComum =
          !isProgramaTce &&
          (statusErroReq === 404 || statusErroReq === 403) &&
          endpoint.includes("/tca-empregador");

        if (deveTentarFallbackTcaEmpregador) {
          try {
            resp = await axios.get(
              `${API_BASE_URL}/tca-empregador/${targetId}/status-completo`,
              {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 15000,
                signal,
              },
            );
          } catch (_) {
            try {
              resp = await axios.get(`${API_BASE_URL}/tca-empregador/${targetId}`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 15000,
                signal,
              });
            } catch (_) {
              throw errReq;
            }
          }
        } else if (deveTentarFallbackTcaComum) {
          try {
            resp = await axios.get(`${API_BASE_URL}/tca/${targetId}`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 15000,
              signal,
            });
          } catch (_) {
            try {
              resp = await axios.get(`${API_BASE_URL}/tca-empregador/${targetId}`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 15000,
                signal,
              });
            } catch (_) {
              throw errReq;
            }
          }
        } else {
          throw errReq;
        }
      }

      const payload = aplicarMetadadosPrograma(resp?.data, programaEfetivo);
      const empresa = normalizarEmpresaResposta(payload) || payload;
      const listaExcecoes = normalizarListaSolicitacoesExcecao(payload, empresa);

      const resultado = {
        payload,
        empresa,
        listaExcecoes,
        fonte: "api",
      };
      return resultado;
    } catch (error) {
      // Qualquer falha na busca individual nunca quebra a UI nem força logout;
      // caímos com segurança no raw já consolidado da listagem.
      const payload = aplicarMetadadosPrograma(raw, programaEfetivo);
      const empresa = normalizarEmpresaResposta(payload) || payload;
      const listaExcecoes = normalizarListaSolicitacoesExcecao(payload, empresa);

      return {
        payload,
        empresa,
        listaExcecoes,
        fonte: "cache",
      };
    } finally {
      inFlightStatusPromises.delete(cacheKey);
    }
  })();

  inFlightStatusPromises.set(cacheKey, promiseExecucao);
  return promiseExecucao;
}

/**
 * Lê a lista de exceções de uma empresa no contexto da listagem atual.
 */
export function obterExcecoesDoItem(item) {
  const raw = item?.raw || {};
  return normalizarListaSolicitacoesExcecao(raw, raw);
}

/**
 * Verifica se o item requer validação SUPEX.
 */
export function obterRequerSupexDoItem(item) {
  const raw = item?.raw || item || {};
  const excecoes = obterExcecoesDoItem(item);
  return cadastroRequerSupex(raw, raw, excecoes, item);
}
